<template>
  <div class="tab-pane fade show active">
    <LoadingIndicatorComponent
      v-if="loading"
      message="Loading premium data..."
    />
    <div v-else class="card">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <div>
          <h5 class="mb-0">Current EAC Premiums & History</h5>
          <small class="text-muted"
            >Latest premium values by category and type</small
          >
        </div>
        <button class="btn btn-primary" @click="showAddPremiumModal = true">
          <i class="fas fa-plus me-2"></i>Add Premium
        </button>
      </div>
      <div class="card-body">
        <!-- Premium Filters -->
        <PremiumFiltersComponent
          v-model:filters="filters"
          :premiumHistory="premiumHistory"
        />

        <!-- Premium Table -->
        <PremiumTableComponent
          :filteredPremiums="filteredPremiumHistory"
          :loading="loading"
          :error="error"
          :deletingIds="deletingPremiums"
          @premium-deleted="fetchPremiumHistory"
          @delete-start="deletingPremiums.add($event)"
          @delete-end="deletingPremiums.delete($event)"
        />
      </div>
    </div>

    <!-- Add Premium Modal -->
    <AddPremiumModalComponent
      v-model="showAddPremiumModal"
      :premiumHistory="premiumHistory"
      @premium-added="fetchPremiumHistory"
    />
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { computed, nextTick, onMounted, ref } from "vue";

import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import AddPremiumModalComponent from "@/market_insights/admin/AddPremiumModalComponent.vue";
import PremiumFiltersComponent from "@/market_insights/admin/PremiumFiltersComponent.vue";
import PremiumTableComponent from "@/market_insights/admin/PremiumTableComponent.vue";
import type { EacPremium } from "@/market_insights/admin/eacPricingTypes";

const { globalErrorHandler } = useGlobalHandlers();

// State
const premiumHistory = ref<EacPremium[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const deletingPremiums = ref(new Set<number>());
const showAddPremiumModal = ref(false);

// Filters
const filters = ref({
  category: "",
  premium_types: [] as string[],
  date_from: "",
  date_to: "",
});

// Filtered premium history
const filteredPremiumHistory = computed(() => {
  let filtered = premiumHistory.value;

  if (filters.value.category) {
    filtered = filtered.filter(
      (premium) => premium.category === filters.value.category
    );
  }

  if (filters.value.premium_types.length > 0) {
    filtered = filtered.filter((premium) =>
      filters.value.premium_types.includes(premium.premium_type)
    );
  }

  if (filters.value.date_from) {
    filtered = filtered.filter(
      (premium) => premium.date.split("T")[0] >= filters.value.date_from
    );
  }

  if (filters.value.date_to) {
    filtered = filtered.filter(
      (premium) => premium.date.split("T")[0] <= filters.value.date_to
    );
  }

  return filtered;
});

const fetchPremiumHistory = async () => {
  loading.value = true;
  error.value = null;
  try {
    const response = await goSolidApi.get("/api/admin/eac-pricing/premiums");
    premiumHistory.value = response.data.data;

    nextTick(() => {
      updateDateRangeFromPremiumData();
    });
  } catch (err: any) {
    error.value = "Failed to fetch premium history";
    globalErrorHandler(err);
  } finally {
    loading.value = false;
  }
};

const updateDateRangeFromPremiumData = () => {
  if (premiumHistory.value.length === 0) return;

  const premiumDates = premiumHistory.value
    .map((premium) => premium.date.split("T")[0])
    .sort();
  const premiumRange = {
    min: premiumDates[0],
    max: premiumDates[premiumDates.length - 1],
  };

  const currentMin = filters.value.date_from;
  const currentMax = filters.value.date_to;

  if (!currentMin || premiumRange.min < currentMin) {
    filters.value.date_from = premiumRange.min;
  }

  if (!currentMax || premiumRange.max > currentMax) {
    filters.value.date_to = premiumRange.max;
  }
};

onMounted(async () => {
  await fetchPremiumHistory();
});
</script>
